import { Button, Container, Field, Heading, Input, Text } from "@chakra-ui/react"
import { useMutation } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { type SubmitHandler, useForm } from "react-hook-form"

import { type ApiError, AuthenticationService } from "../client"
import useCustomToast from "../hooks/useCustomToast"
import { emailPattern, handleError } from "../utils"

interface FormData {
  email: string
}

export const Route = createFileRoute("/recover-password")({
  component: RecoverPassword,
  beforeLoad: async () => {
    const token = localStorage.getItem("access_token")
    if (token) {
      throw redirect({
        to: "/",
      })
    }
  },
})

function RecoverPassword() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>()
  const showToast = useCustomToast()

  const recoverPassword = async (data: FormData) => {
    await AuthenticationService.recoverPassword({
      email: data.email,
    })
  }

  const mutation = useMutation({
    mutationFn: recoverPassword,
    onSuccess: () => {
      showToast(
        "E-Mail gesendet",
        "Eine E-Mail mit einem Link zur Wiederherstellung des Passworts wurde gesendet",
        "success",
      )
      reset()
    },
    onError: (err: ApiError) => {
      handleError(err, showToast)
    },
  })

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    mutation.mutate(data)
  }

  return (
    <Container
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      h="100vh"
      maxW="sm"
      alignItems="stretch"
      justifyContent="center"
      gap={4}
      centerContent
      textAlign="center"
    >
      <Heading size="4xl" color="main"  mb={2}>
        Passwort vergessen
      </Heading>
      <Text>
        Eine E-Mail zur Wiederherstellung des Passworts wird an das registrierte
        Konto gesendet.
      </Text>
      <Field.Root invalid={!!errors.email}>
        <Input
          id="email"
          {...register("email", {
            required: "E-Mail ist erforderlich",
            pattern: emailPattern,
          })}
          placeholder="E-Mail"
          type="email"
        />
        {errors.email && (
          <Field.ErrorText>{errors.email.message}</Field.ErrorText>
        )}
      </Field.Root>
      <Button variant="primary" type="submit" loading={isSubmitting}>
        Weiter
      </Button>
    </Container>
  )
}
